/**
 * The desktop chat through an AI provider's official API (Claude, OpenAI) with the person's own key,
 * the AI driving Vephus with tools (lib/apiChat.ts). Reads run at once; anything that changes
 * something is a card the person confirms or declines, and the answer goes back to the AI.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Check, KeyRound, Loader2, Play, RotateCcw, Send, Wrench, X } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { usePromptCatalog, runPrompt } from '../lib/prompts'
import { aiKeyHas } from '../lib/localAgent'
import { ANTHROPIC_MODELS, sendTurn, systemFor, toolByName, type Conversation, type Provider } from '../lib/apiChat'
import { makeToolRunner } from '../lib/apiChatTools'
import { aiRequest } from '../lib/desktop'
import type { RepositoryLink } from '../lib/types'

type Entry =
  | { id: string; role: 'user'; text: string }
  | { id: string; role: 'ai'; text: string }
  | { id: string; role: 'tool'; name: string; result: string }
  | { id: string; role: 'confirm'; name: string; input: Record<string, unknown>; state: 'asking' | 'yes' | 'no' }
  | { id: string; role: 'note'; text: string; bad?: boolean }

const SETTINGS_KEY = 'devpulse.apiChat'
const id = () => crypto.randomUUID()

interface Settings {
  provider: Provider
  model: string
  effort: 'low' | 'medium' | 'high'
}

function readSettings(): Settings {
  try {
    const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? 'null') as Partial<Settings> | null
    return { provider: 'anthropic', model: ANTHROPIC_MODELS[0], effort: 'medium', ...(saved ?? {}) }
  } catch {
    return { provider: 'anthropic', model: ANTHROPIC_MODELS[0], effort: 'medium' }
  }
}

/** One conversation per repository while the window is open. */
const sessions = new Map<string, { conversation: Conversation | null; entries: Entry[] }>()

export function ApiChat({ repository, folder, branch }: { repository: RepositoryLink; folder: string | null; branch: string | null }) {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const [settings, setSettings] = useState<Settings>(readSettings)
  const [entries, setEntries] = useState<Entry[]>(() => sessions.get(repository.id)?.entries ?? [])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const answers = useRef(new Map<string, (yes: boolean) => void>())
  const bottom = useRef<HTMLDivElement>(null)
  const { data: catalog } = usePromptCatalog()
  const { data: hasKey } = useQuery({ queryKey: ['ai-key', settings.provider], queryFn: () => aiKeyHas(settings.provider) })
  const { data: teammates } = useQuery({ queryKey: ['teammates', repository.id], queryFn: () => api.workOrders.teammates(repository.id), retry: false })
  const template = useMemo(() => catalog?.prompts.find((p) => p.key === 'tool.desktop-api-chat')?.body ?? null, [catalog])

  useEffect(() => setEntries(sessions.get(repository.id)?.entries ?? []), [repository.id])
  // Braces: in recent WebView2 builds scrollIntoView returns a Promise, which React would take as the clean-up.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [entries])

  const save = (next: Settings) => {
    setSettings(next)
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(next))
    } catch {
      // Private storage: the choice lasts until the window closes.
    }
  }

  const push = (entry: Entry) => {
    const state = sessions.get(repository.id) ?? { conversation: null, entries: [] }
    state.entries = [...state.entries, entry]
    sessions.set(repository.id, state)
    setEntries(state.entries)
  }
  const patch = (entryId: string, change: Partial<Entry>) => {
    const state = sessions.get(repository.id)
    if (!state) return
    state.entries = state.entries.map((e) => (e.id === entryId ? ({ ...e, ...change } as Entry) : e))
    setEntries(state.entries)
  }

  const send = async () => {
    const message = text.trim()
    if (!message || busy || !template) return
    setText('')
    push({ id: id(), role: 'user', text: message })
    const state = sessions.get(repository.id) ?? { conversation: null, entries: [] }
    // A new conversation, or a change of provider/model/effort, starts a fresh history: each provider
    // keeps its own shape, and the model checks its own past turns.
    if (!state.conversation || state.conversation.provider !== settings.provider || state.conversation.model !== settings.model || state.conversation.effort !== settings.effort) {
      state.conversation = {
        provider: settings.provider,
        model: settings.model,
        effort: settings.effort,
        system: systemFor(template, { repository: repository.name, branch, teammates: (teammates ?? []).map((m) => m.name) }),
        messages: [],
      }
    }
    sessions.set(repository.id, state)
    setBusy(true)
    try {
      const outcome = await sendTurn(state.conversation, message, {
        request: aiRequest,
        runTool: makeToolRunner({
          repositoryId: repository.id,
          folder,
          navigate,
          runAudit: async (key) => {
            const prompt = catalog?.prompts.find((p) => p.key === key)
            if (!prompt) return `No audit called ${key}.`
            const order = await runPrompt(prompt, repository.id, locale)
            return `Audit ${key} started (work order ${order.id}).`
          },
        }),
        confirm: (name, input) =>
          new Promise<boolean>((resolve) => {
            const entryId = id()
            answers.current.set(entryId, resolve)
            push({ id: entryId, role: 'confirm', name, input, state: 'asking' })
          }),
        onText: (said) => push({ id: id(), role: 'ai', text: said }),
        onTool: (name, _input, result) => {
          if (!toolByName(name)?.mutating) push({ id: id(), role: 'tool', name, result: result.slice(0, 160) })
        },
      })
      if (outcome.stopReason === 'refusal') push({ id: id(), role: 'note', text: t('apiChat.refused'), bad: true })
      if (outcome.stopReason === 'max_tokens') push({ id: id(), role: 'note', text: t('apiChat.cut'), bad: true })
      if (outcome.stopReason === 'max_steps') push({ id: id(), role: 'note', text: t('apiChat.tooManySteps') })
    } catch (error) {
      const message = String(error instanceof Error ? error.message : error)
      push({ id: id(), role: 'note', text: message.includes('no-key') ? t('apiChat.noKey') : message, bad: true })
    } finally {
      setBusy(false)
    }
  }

  const answer = (entryId: string, yes: boolean) => {
    answers.current.get(entryId)?.(yes)
    answers.current.delete(entryId)
    patch(entryId, { state: yes ? 'yes' : 'no' })
  }

  return (
    <div className="local-chat">
      <div className="row row--tight row--wrap api-chat__settings">
        <select className="select select--sm" value={settings.provider} aria-label={t('apiChat.provider')}
          onChange={(e) => {
            const provider = e.target.value as Provider
            save({ ...settings, provider, model: provider === 'anthropic' ? ANTHROPIC_MODELS[0] : '' })
          }}>
          <option value="anthropic">Claude (Anthropic API)</option>
          <option value="openai">OpenAI API</option>
        </select>
        {settings.provider === 'anthropic' ? (
          <select className="select select--sm" value={settings.model} aria-label={t('apiChat.model')} onChange={(e) => save({ ...settings, model: e.target.value })}>
            {ANTHROPIC_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        ) : (
          <input className="input input--sm" style={{ width: 160 }} value={settings.model} placeholder={t('apiChat.openaiModel')} aria-label={t('apiChat.model')} onChange={(e) => save({ ...settings, model: e.target.value.trim() })} />
        )}
        {settings.provider === 'anthropic' && (
          <select className="select select--sm" value={settings.effort} aria-label={t('apiChat.effort')} title={t('apiChat.effortHint')} onChange={(e) => save({ ...settings, effort: e.target.value as Settings['effort'] })}>
            <option value="low">{t('apiChat.effort.low')}</option>
            <option value="medium">{t('apiChat.effort.medium')}</option>
            <option value="high">{t('apiChat.effort.high')}</option>
          </select>
        )}
        {hasKey === false && (
          <Link to="/agents/connect?tab=desktop" className="tiny api-chat__nokey"><KeyRound size={12} /> {t('apiChat.addKey')}</Link>
        )}
      </div>

      <div className="local-chat__log" aria-live="polite">
        {entries.length === 0 && <div className="local-chat__empty small muted">{t('apiChat.empty', { name: repository.name })}</div>}
        {entries.map((entry) => {
          if (entry.role === 'user') return <div key={entry.id} className="local-chat__msg is-user">{entry.text}</div>
          if (entry.role === 'ai') return <div key={entry.id} className="local-chat__msg is-ai">{entry.text}</div>
          if (entry.role === 'tool') return <div key={entry.id} className="local-chat__tool tiny mono"><Wrench size={11} /> {entry.name} <span className="muted">· {entry.result}</span></div>
          if (entry.role === 'note') return <div key={entry.id} className={`local-chat__note tiny${entry.bad ? ' is-bad' : ''}`}>{entry.text}</div>
          return (
            <div key={entry.id} className={`local-chat__action is-${entry.state === 'no' ? 'skipped' : entry.state === 'yes' ? 'done' : 'proposed'}`}>
              <div className="small"><strong>{t(`apiChat.tool.${entry.name}`)}</strong></div>
              <pre className="tiny muted api-chat__input">{summary(entry.input)}</pre>
              {entry.state === 'asking' ? (
                <div className="row row--tight">
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => answer(entry.id, true)}><Play size={12} /> {t('chat.run')}</button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => answer(entry.id, false)}><X size={12} /> {t('apiChat.decline')}</button>
                </div>
              ) : (
                <span className="tiny muted">{entry.state === 'yes' ? <><Check size={12} /> {t('apiChat.confirmed')}</> : t('apiChat.declined')}</span>
              )}
            </div>
          )
        })}
        <div ref={bottom} />
      </div>

      <div className="local-chat__compose">
        <textarea className="input" rows={2} value={text} placeholder={t('chat.placeholder')} aria-label={t('chat.placeholder')}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send()
            }
          }} />
        <div className="row row--tight">
          <button type="button" className="btn btn--primary btn--sm" onClick={() => void send()} disabled={!text.trim() || busy || !template || !settings.model}>
            {busy ? <Loader2 size={12} className="spin" /> : <Send size={12} />} {t('dock.send')}
          </button>
          <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => { sessions.delete(repository.id); setEntries([]) }}>
            <RotateCcw size={12} /> {t('chat.new')}
          </button>
        </div>
      </div>
    </div>
  )
}

function summary(input: Record<string, unknown>): string {
  return Object.entries(input)
    .map(([k, v]) => `${k}: ${typeof v === 'string' ? (v.length > 300 ? `${v.slice(0, 299)}…` : v) : JSON.stringify(v)}`)
    .join('\n')
}
